import { Component, OnDestroy, OnInit } from '@angular/core';
import { BiddingUtils, reconcileHighBidder } from '@/vb-utils/bidding.util';
import {
    Subject,
    debounceTime,
    distinctUntilChanged,
    first,
    interval,
    map,
    takeUntil,
} from 'rxjs';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/viewbid/state/app-state';
import { selectUserBidHistoryState } from '../../state/bidding/user-bid-history.selectors';
import { UserBidHistoryState } from '../../state/bidding/user-bid-history.reducer';
import { ProductService } from '@/shared/services/product.service';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { loadUserBidHistory } from '../../state/bidding/user-bid-history.actions';
import { UserBidHistory } from '../../model/userBidHistory';
import { SocketService } from 'src/app/viewbid/shared/services/socket.service';
import { ServerClockService } from 'src/app/viewbid/shared/services/server-clock.service';
import { PostBidLotState } from '@viewbid/ts-angular-client';
import { VbSnackbarService } from '@viewbid/ui';
import { DeviceCheckService } from 'src/app/viewbid/shared/services/device-check.service';
import { TimerService } from '@/shared/services/timerService';
import { LotCountdownTracker } from 'src/app/viewbid/shared/utils/lot-countdown.util';
import {
    isBidLotOpen,
    userBidHistoryToBidTableRows,
} from 'src/app/viewbid/shared/utils/bid-history-ui-mapper.util';
import { LotCardBidService } from 'src/app/viewbid/shared/services/lot-card-bid.service';
import { VbBidTableRow, VbLegendItem } from '@viewbid/ui';
import { TranslateService } from '@ngx-translate/core';

import {
    navigateProfilePaging,
    ProfilePaging,
    resolveProfilePaging,
} from 'src/app/viewbid/shared/utils/profile-paging.util';

@Component({
    selector: 'vb-user-bids',
    templateUrl: './user-bids.component.html',
    styleUrls: ['./user-bids.component.scss'],
})
export class UserBidsComponent implements OnInit, OnDestroy {
    userBidHistoryResponse: UserBidHistory[] = [];
    /** vb-bid-table rows — rebuilt (fresh array ref) on data changes + tick. */
    tableRows: VbBidTableRow[] = [];
    currentUserId: string = '';
    public pageNo: number = 1;
    public pageSize: number = 10;
    private readonly defaultPageSize: number = this.pageSize;
    public readonly pageSizeOptions: ReadonlyArray<number> = [10, 25, 50];
    public paginate: any = {}; // Pagination use only
    public isLoading: boolean = true;
    searchQuery: string = '';
    public isMobile: boolean = false;

    /** Default server sort: lots closing soonest first. Same orderBy shape
     *  the old Closing-column sort sent — the API sorts lot end via the
     *  auction's toDatetime (there is no lot-level orderBy for end time). */
    private static readonly ORDER_BY_CLOSING_SOONEST = '{"toDatetime" : "asc"}';

    /** Footer legend for the table's status accents. (Proxy bids are
     *  self-labeled in the Your-max cell — no legend entry needed.) */
    public readonly legendItems: VbLegendItem[] = [
        { tone: 'positive', label: 'Winning / won' },
        { tone: 'warn', label: 'Losing' },
        { tone: 'danger', label: 'Lost' },
        { tone: 'neutral', label: 'Reserve not met' },
    ];

    /** Staged-bid state for the shared confirm flow (mirrors the lot list
     *  page): the row's quick bid is held here while vb-bid-confirm-dialog
     *  is open, then posted via LotCardBidService. */
    confirmDialogOpen: boolean = false;
    pendingBidAmount: number = 0;
    pendingBidBpPct: number = 0;
    private pendingBidLotId: string | null = null;

    /** Lots with a bid in flight, keyed by auctionLotId — drives each row's
     *  CTA spinner independently (mirrors the lot list page). */
    private bidLoadingByLot: Record<string, boolean> = {};

    countdownTracker: LotCountdownTracker;
    private socketSubscribed: boolean = false;
    private searchSubject: Subject<string> = new Subject();
    private destroy$ = new Subject<void>();
    /** Lot and auction rooms already requested this session (join once each). */
    private readonly subscribedRooms: Set<string> = new Set<string>();

    constructor(
        private biddingUtils: BiddingUtils,
        private store: Store<AppState>,
        public productService: ProductService,
        private route: ActivatedRoute,
        private router: Router,
        private readonly lotCardBidService: LotCardBidService,
        private readonly socketService: SocketService,
        private readonly snackbarService: VbSnackbarService,
        private readonly deviceCheckService: DeviceCheckService,
        private readonly timerService: TimerService,
        private readonly translate: TranslateService,
        private readonly serverClock: ServerClockService,
    ) {
        this.countdownTracker = new LotCountdownTracker(this.timerService, () =>
            this.serverClock.now(),
        );
    }

    ngOnInit(): void {
        this.countdownTracker.start();

        this.biddingUtils
            .getCurrentUserId()
            .pipe(first())
            .subscribe((userId: string) => {
                this.currentUserId = userId;
                // Row statuses compare against the user id — re-derive once
                // it arrives (the first page can land before it).
                this.refreshTableRows();
            });

        this.route.queryParams
            .pipe(
                map((params: Params): ProfilePaging => {
                    navigateProfilePaging(
                        this.router,
                        this.route,
                        params,
                        this.defaultPageSize,
                        true,
                    );
                    return resolveProfilePaging(params, this.defaultPageSize);
                }),
                distinctUntilChanged(
                    (
                        previous: ProfilePaging,
                        current: ProfilePaging,
                    ): boolean =>
                        previous.page === current.page &&
                        previous.pageSize === current.pageSize,
                ),
                takeUntil(this.destroy$),
            )
            .subscribe((paging: ProfilePaging) => {
                this.pageNo = paging.page;
                this.pageSize = paging.pageSize;
                this.loadPage(this.pageNo);
            });

        this.store
            .select(selectUserBidHistoryState)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (bidHistory: UserBidHistoryState) => {
                    this.isLoading = bidHistory.loading;
                    if (bidHistory && bidHistory.userBidHistory?.data) {
                        const paging: ProfilePaging = resolveProfilePaging(
                            { page: this.pageNo, pageSize: this.pageSize },
                            this.defaultPageSize,
                            bidHistory.userBidHistory.totalCount ?? 0,
                        );
                        if (paging.page !== this.pageNo) {
                            this.isLoading = true;
                            navigateProfilePaging(
                                this.router,
                                this.route,
                                paging,
                                this.defaultPageSize,
                                true,
                            );
                            return;
                        }
                        if (bidHistory.userBidHistory.data.length > 0) {
                            this.userBidHistoryResponse = bidHistory
                                .userBidHistory.data as UserBidHistory[];
                            this.paginate = this.productService.getPager(
                                bidHistory.userBidHistory.totalCount ?? 0,
                                this.pageNo,
                                this.pageSize,
                                false,
                            );
                            this.subscribeToSocketUpdates();
                            this.subscribeToRowRooms();
                        } else {
                            this.userBidHistoryResponse = [];
                            this.paginate = this.productService.getPager(
                                0,
                                0,
                                this.pageSize,
                                false,
                            );
                            this.paginate.currentPage = 0;
                        }
                        this.syncCountdownLots();
                        this.refreshTableRows();
                    }
                },
                error: () => {
                    this.isLoading = false;
                },
            });

        // Reconnect REST backstop: reload the current page on non-recovered reconnect.
        this.socketService.reconnected$
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.loadPage(this.pageNo));

        this.searchSubject
            .pipe(
                debounceTime(500),
                distinctUntilChanged(),
                takeUntil(this.destroy$),
            )
            .subscribe((searchText: string) => {
                this.searchQuery =
                    searchText.length > 0 ? searchText.trim() : '';
                if (
                    !navigateProfilePaging(
                        this.router,
                        this.route,
                        { page: 1, pageSize: this.pageSize },
                        this.defaultPageSize,
                    )
                ) {
                    this.loadPage(this.pageNo);
                }
            });

        this.deviceCheckService.isMobile$
            .pipe(takeUntil(this.destroy$))
            .subscribe((isMobile: boolean) => {
                this.isMobile = isMobile;
            });
        this.deviceCheckService.isSlow().then((isSlow: boolean) => {
            if (isSlow) {
                this.snackbarService.system(
                    this.translate.instant(
                        'Notifications.slowConnectionDetected',
                    ),
                );
            }
        });

        // Parent-owned 1s tick: re-read the tracker's per-lot seconds into
        // fresh row refs so the OnPush table repaints its countdowns.
        interval(1000)
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.refreshTableRows());

        // A lot's clock crossing zero flips its row to closed (won/lost from
        // the final high bidder) on the same tick, not the next one.
        this.countdownTracker.lotEnded$
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.refreshTableRows());
    }

    setPage(page: number): void {
        navigateProfilePaging(
            this.router,
            this.route,
            { page, pageSize: this.pageSize },
            this.defaultPageSize,
        );
    }

    setPageSize(pageSize: number): void {
        navigateProfilePaging(
            this.router,
            this.route,
            { page: 1, pageSize },
            this.defaultPageSize,
        );
    }

    private loadPage(page: number): void {
        if (page <= 0) return;
        this.store.dispatch(
            loadUserBidHistory({
                page,
                pageSize: this.pageSize,
                query: this.searchQuery,
                orderBy: UserBidsComponent.ORDER_BY_CLOSING_SOONEST,
            }),
        );
    }

    /** vb-search-input emits per keystroke; the subject keeps the 500ms debounce. */
    onSearchChange(value: string): void {
        this.searchSubject.next(value);
    }

    /** Row body / closed-row "View lot" → the same lot deep-link the old table used.
     *  Closed lots are only viewable by their winner — the table already
     *  renders non-navigable rows inert; this guard is defence in depth. */
    onRowNavigate(row: VbBidTableRow): void {
        if (row.navigable === false) return;
        const item: UserBidHistory | undefined = this.findRowSource(row.id);
        if (!item?.auctionId || !item?.lotId) return;
        this.router.navigate(['/auctions', item.auctionId, 'lot', item.lotId]);
    }

    /** Open rows' "Bid · $X" → stage the quick bid through the shared confirm
     *  flow — the same mechanics as the lot card / lot details page. */
    onBidClick(row: VbBidTableRow): void {
        const item: UserBidHistory | undefined = this.findRowSource(row.id);
        const amount: number = Number(item?.minimumBidAmount ?? 0);
        if (!item?.auctionId || !(amount > 0)) return;
        if (!this.currentUserId) {
            this.snackbarService.dismissAll();
            this.snackbarService.error(
                this.translate.instant('Bidding.loginAgainToContinueBidding'),
            );
            return;
        }
        this.pendingBidLotId = row.id;
        this.pendingBidAmount = amount;
        this.pendingBidBpPct = Number(item.buyerPremium ?? 0);
        if (this.biddingUtils.shouldShowBidConfirmation(item.auctionId)) {
            this.confirmDialogOpen = true;
        } else {
            this.placePendingBid();
        }
    }

    onBidConfirmed(dontAsk: boolean): void {
        const item: UserBidHistory | null = this.pendingBidSource;
        if (dontAsk && item?.auctionId) {
            this.biddingUtils.setSkipBidConfirmation(item.auctionId);
        }
        this.confirmDialogOpen = false;
        this.placePendingBid();
    }

    onBidCancelled(): void {
        this.confirmDialogOpen = false;
        this.pendingBidLotId = null;
        this.pendingBidAmount = 0;
        this.pendingBidBpPct = 0;
    }

    /** Wire row backing the staged bid — re-resolved by id so socket patches
     *  that land while the confirm dialog is open stay authoritative. */
    private get pendingBidSource(): UserBidHistory | null {
        if (!this.pendingBidLotId) return null;
        return this.findRowSource(this.pendingBidLotId) ?? null;
    }

    private findRowSource(auctionLotId: string): UserBidHistory | undefined {
        return this.userBidHistoryResponse.find(
            (item: UserBidHistory) => item.auctionLotId === auctionLotId,
        );
    }

    /**
     * Lot updates arrive per-lot over socket rooms, and this page's rows are
     * not guaranteed to be covered by the boot-time watcher snapshot (lots bid
     * on mid-session, un-watched lots with old bids, bids from another
     * device). Subscribe to the room of every row on screen, like the
     * watchlist page does. The anti-snipe fan-out (UPDATE_TIMES_EVENT) is
     * broadcast to the auction's room rather than the lot's, so each row's
     * auction room is joined as well. Rooms are never left: other surfaces
     * (watchlist, header counts) may rely on the same memberships.
     */
    private subscribeToRowRooms(): void {
        const roomIds: string[] = [];
        for (const row of this.userBidHistoryResponse) {
            for (const id of [row.auctionLotId, row.auctionId]) {
                if (id && !this.subscribedRooms.has(id)) {
                    this.subscribedRooms.add(id);
                    roomIds.push(id);
                }
            }
        }
        if (!roomIds.length) return;
        this.socketService.subscribeToResources(
            roomIds.map((resourceId: string) => ({
                resourceId,
                listen: true,
            })),
        );
    }

    private subscribeToSocketUpdates(): void {
        // The store emits on every page load; subscribe to the resource
        // stream once per component life.
        if (this.socketSubscribed) return;
        this.socketSubscribed = true;
        this.socketService.resource$
            .pipe(takeUntil(this.destroy$))
            .subscribe((data: string) => {
                const eventResponse: any = JSON.parse(data);

                // User-targeted event (personal room, all devices): keeps the
                // "Your max" cell live when this user bids from another
                // device — the lot-room broadcasts below never carry it.
                if (eventResponse.action === 'USER_MAX_BID_UPDATE') {
                    const userMaxBid: number = eventResponse.data.userMaxBid;
                    this.userBidHistoryResponse =
                        this.userBidHistoryResponse.map(
                            (bidHistory: UserBidHistory) => {
                                if (
                                    bidHistory.auctionLotId !==
                                    eventResponse.data.auctionLotId
                                ) {
                                    return bidHistory;
                                }
                                // This event is sent before the PAGE_LOAD
                                // broadcast, so the row's currentBid is still
                                // pre-bid; prefer the payload's lot snapshot.
                                const currentPrice: number =
                                    eventResponse.data.lot?.currentBid ??
                                    bidHistory.currentBid ??
                                    0;
                                return {
                                    ...bidHistory,
                                    userBid: userMaxBid,
                                    // The server derives isProxy with the same
                                    // rule as the bid-history load. Fallback
                                    // (older payloads): a max bid above the
                                    // current price proves a proxy; equality
                                    // proves nothing (a proxy driven to its
                                    // ceiling sits at max == current), so
                                    // never downgrade the flag.
                                    isProxy:
                                        eventResponse.data.isProxy ??
                                        (userMaxBid > currentPrice
                                            ? true
                                            : bidHistory.isProxy),
                                };
                            },
                        );
                    this.refreshTableRows();
                    return;
                }

                // Anti-snipe fan-out rides the auction room: every open lot
                // in the sniped lot's group (or the whole auction when live)
                // gets a new close time.
                if (eventResponse.action === 'UPDATE_TIMES_EVENT') {
                    this.applyUpdatedTimes(eventResponse.data?.lots);
                    return;
                }

                // PAGE_LOAD and NEW_HIGH_BID carry the same lot-state payload
                // and get the same row patch.
                if (
                    eventResponse.action !== 'PAGE_LOAD' &&
                    eventResponse.action !== 'NEW_HIGH_BID'
                ) {
                    return;
                }
                this.userBidHistoryResponse = this.userBidHistoryResponse.map(
                    (bidHistory: UserBidHistory) => {
                        if (
                            bidHistory.auctionLotId !==
                            eventResponse.data.auctionLotsId
                        ) {
                            return bidHistory;
                        }
                        return {
                            ...bidHistory,
                            currentHighBidderId: eventResponse.data.highBidder,
                            minimumBidAmount: eventResponse.data.minimumBid,
                            currentBid: eventResponse.data.currentPrice,
                            lotToDatetime: eventResponse.data.endDate,
                        };
                    },
                );
                // Soft-close extensions ride on endDate — reseed the tracker,
                // then re-map so the table reflects the patch immediately.
                this.syncCountdownLots();
                this.refreshTableRows();
            });
    }

    /** Posts the staged bid through the shared LotCardBidService (same
     *  toasts/error handling as every other bidding surface). */
    private placePendingBid(): void {
        const item: UserBidHistory | null = this.pendingBidSource;
        const amount: number = this.pendingBidAmount;
        const bpPct: number = this.pendingBidBpPct;
        this.pendingBidLotId = null;
        this.pendingBidAmount = 0;
        this.pendingBidBpPct = 0;
        if (!item?.auctionId || !item.auctionLotId || !this.currentUserId) {
            return;
        }
        const auctionLotId: string = item.auctionLotId;
        // The lot may have closed while the confirm dialog was open — don't
        // land a bid after the clock has run out (mirrors the lot list page).
        if (!isBidLotOpen(item.lotToDatetime, this.serverClock.now())) return;
        if (this.bidLoadingByLot[auctionLotId]) return;
        this.setBidLoading(auctionLotId, true);
        this.refreshTableRows();
        this.lotCardBidService.placeConfirmedBid(
            {
                auctionId: item.auctionId,
                auctionLotsId: auctionLotId,
                lotId: item.lotId ?? (item as { id?: string })?.id,
                lotTitle: item.title ?? '',
                lotNumber: item.lotNumber,
                buyerPremium: bpPct,
                amount,
                minimumBidAmount: amount,
                pageName: 'LOT_DISPLAY',
                pageLocation: 'QUICK_BID',
            },
            this.currentUserId,
            (placed: boolean, lotState?: PostBidLotState) => {
                this.setBidLoading(auctionLotId, false);
                if (placed) {
                    this.applyBidResult(auctionLotId, amount, lotState);
                }
                // Optimistic row patch → table (socket + lotState reconcile).
                this.syncCountdownLots();
                this.refreshTableRows();
            },
        );
    }

    /** Per-lot in-flight flag — fresh map ref so change detection sees it. */
    private setBidLoading(auctionLotId: string, loading: boolean): void {
        this.bidLoadingByLot = {
            ...this.bidLoadingByLot,
            [auctionLotId]: loading,
        };
    }

    /** Patches the bid's row with the post-bid lot state (or an optimistic
     *  high-bidder patch when the response carried none). */
    private applyBidResult(
        auctionLotId: string,
        amount: number,
        lotState?: PostBidLotState,
    ): void {
        this.userBidHistoryResponse = this.userBidHistoryResponse.map(
            (row: UserBidHistory) => {
                if (row.auctionLotId !== auctionLotId) {
                    return row;
                }
                if (lotState) {
                    return {
                        ...row,
                        currentBid: lotState.currentBid,
                        minimumBidAmount: lotState.minimumNextBid,
                        userBid: amount,
                        lotToDatetime: lotState.toDatetime,
                        currentHighBidderId:
                            reconcileHighBidder(
                                row.currentHighBidderId,
                                this.currentUserId,
                                lotState.isHighBidder,
                            ) ?? undefined,
                        isProxy: false,
                    };
                }
                return {
                    ...row,
                    currentBid: amount,
                    userBid: amount,
                    currentHighBidderId: this.currentUserId,
                    isProxy: false,
                };
            },
        );
    }

    /** Seeds the tracker with the page's end clocks. Bid rows are never
     *  upcoming (you can't bid on an unstarted lot), so fromDatetime is
     *  omitted on purpose — seconds always count to lotToDatetime. */
    private syncCountdownLots(): void {
        this.countdownTracker.setLots(
            this.userBidHistoryResponse.map((item: UserBidHistory) => ({
                auctionLotId: item.auctionLotId ?? '',
                toDatetime: item.lotToDatetime,
            })),
        );
    }

    /** Patches each listed row's close time from an UPDATE_TIMES_EVENT and
     *  reseeds the tracker, so extended lots neither snap back to the old
     *  clock nor flip to closed at it. */
    private applyUpdatedTimes(
        lots: { auctionLotId?: string; endDate?: string }[] | undefined,
    ): void {
        if (!Array.isArray(lots)) return;
        const endDateByLot: Map<string, string> = new Map<string, string>();
        for (const lot of lots) {
            if (lot.auctionLotId && lot.endDate) {
                endDateByLot.set(lot.auctionLotId, lot.endDate);
            }
        }
        let touched: boolean = false;
        this.userBidHistoryResponse = this.userBidHistoryResponse.map(
            (row: UserBidHistory) => {
                const endDate: string | undefined = row.auctionLotId
                    ? endDateByLot.get(row.auctionLotId)
                    : undefined;
                if (!endDate || endDate === row.lotToDatetime) return row;
                touched = true;
                return {
                    ...row,
                    lotToDatetime: endDate,
                };
            },
        );
        if (!touched) return;
        this.syncCountdownLots();
        this.refreshTableRows();
    }

    /** Re-maps wire rows → table rows (fresh refs). */
    private refreshTableRows(): void {
        this.tableRows = userBidHistoryToBidTableRows(
            this.userBidHistoryResponse,
            this.currentUserId,
            this.serverClock.now(),
            this.countdownTracker.secondsLeft,
        ).map((row: VbBidTableRow) =>
            this.bidLoadingByLot[row.id]
                ? { ...row, actionLoading: true }
                : row,
        );
        // Paging/search can swap the rows out from under the open confirm
        // dialog — drop the staged bid so it can't land on a later page.
        if (
            this.pendingBidLotId &&
            !this.tableRows.some(
                (row: VbBidTableRow) => row.id === this.pendingBidLotId,
            )
        ) {
            this.onBidCancelled();
        }
    }

    ngOnDestroy(): void {
        this.countdownTracker.stop();
        this.destroy$.next();
        this.destroy$.complete();
    }
}
